import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, IconButton, Snackbar } from '@mui/material'
import * as React from 'react'
import { useTranslate } from 'react-admin'
import { useVersionCheck } from '../hooks/useVersionCheck'
import { hardRefresh } from '../utils/hardRefresh'

/**
 * Non-blocking notification that appears when /vet-portal/VERSION.txt reports
 * a newer SHA than the running bundle. Action button reloads the page.
 *
 * Intentionally does NOT auto-reload while the staleness is fresh: a forced
 * reload could lose unsaved form input or interrupt a vet mid-chart-review.
 * Escalation lives in ForceReloadDialog — after 24h of ignored nudges (or the
 * operator force_before kill-switch) the reload stops being optional, because
 * long-stale bundles can carry known-fixed bugs that harm the whole platform
 * (2026-07-09: a 13-day-stale tab's refetch loop stalled prod logins).
 */
export const UpdateAvailableBanner: React.FC = () => {
  const translate = useTranslate()
  const { updateAvailable, forceReload, deployedSha, runningSha } = useVersionCheck()
  const [dismissed, setDismissed] = React.useState(false)

  // Reset dismissal when a NEW deploy lands so the banner reappears even
  // after the user dismissed an earlier one.
  const lastSeenDeployedSha = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (deployedSha && deployedSha !== lastSeenDeployedSha.current) {
      lastSeenDeployedSha.current = deployedSha
      setDismissed(false)
    }
  }, [deployedSha])

  // Yield to ForceReloadDialog once escalated — showing both is noise.
  const open = updateAvailable && !dismissed && !forceReload

  const handleRefresh = () => {
    void hardRefresh(deployedSha)
  }

  const handleDismiss = (_event?: unknown, reason?: string) => {
    // Don't dismiss on click-away — the message is important enough to require
    // explicit dismissal or refresh.
    if (reason === 'clickaway') return
    setDismissed(true)
  }

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      onClose={handleDismiss}
      message={
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#1976d2' }}>ℹ️</span>
          <span>
            {translate('pos.app_update.banner_message', {
              versions:
                deployedSha && runningSha !== 'unknown' ? ` (${runningSha} → ${deployedSha})` : '',
            })}
          </span>
        </span>
      }
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Button
            color="inherit"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{
              fontWeight: 'bold',
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
              },
            }}
          >
            {translate('pos.app_update.refresh')}
          </Button>
          <IconButton
            size="small"
            color="inherit"
            aria-label={translate('pos.common.close')}
            onClick={handleDismiss}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      }
      ContentProps={{
        sx: {
          backgroundColor: '#e3f2fd',
          color: '#1565c0',
          border: '1px solid #bbdefb',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      }}
    />
  )
}

export default UpdateAvailableBanner
